import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type {
  BlockEditorComponent,
  BlockValues,
} from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { ComponentMeta } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { DefaultEditor } from "./DefaultEditor";
import { useBlockFormWatch } from "./hooks/useBlockFormWatch";
import { blockToValues } from "./utils/blockToValues";

export interface EditorComponents {
  [key: string]: BlockEditorComponent;
}

export interface EditorFormProps {
  meta: ComponentMeta;
  selectedBlock: BaseBlock;
}

export const EditorForm = ({ selectedBlock, meta }: EditorFormProps) => {
  // Combined subscription for all editor form data
  const { components, currentCanvasTheme, appearance, canvasSize } =
    useBuilderStore(state => ({
      components: state.getBlockEditors(),
      currentCanvasTheme: state.currentCanvasTheme,
      appearance: state.appearance,
      canvasSize: state.canvasSize,
    }));

  const EditorComponent = components[selectedBlock.type] || DefaultEditor;

  const values = useMemo(
    () =>
      blockToValues({
        block: selectedBlock,
        currentCanvasTheme,
        appearance,
        canvasSize,
      }),
    [selectedBlock, currentCanvasTheme, appearance, canvasSize],
  );

  const methods = useForm<BlockValues>({
    defaultValues: values,
    mode: "onChange",
  });

  const { reset, watch } = methods;

  useEffect(() => {
    reset(values);
  }, [reset, values, selectedBlock.props]);

  useBlockFormWatch({ watch });

  return (
    <FormProvider {...methods}>
      <EditorComponent block={selectedBlock} meta={meta} values={values} />
    </FormProvider>
  );
};
